import produce from "immer";
import { orderBy } from "lodash";
import { GetState, SetState } from "zustand";

import {
  ReportFragment,
  ViewerFragment,
} from "../../graphql/generated/definitions";
import { SectionWithAnswers } from "../../components/reports/types/ReportFormTypes";
import { ReportRole, ReportState } from "../../types";
import { State } from "../../store";

export type ReportStep = "welcome" | "report" | "success" | "leaving" | null;

export type ReportStore = {
  report: {
    sections: SectionWithAnswers[];
    /**
     * Progress on the completion of a report, range from 0 to 100
     */
    progress: number;
    /**
     * Map of questionId / boolean tracking if a particular question has been filled or not
     */
    progressMap: Record<string, boolean>;
    /**
     * Set a question filled state
     *
     * Automatically set progress on the completion of a report
     */
    setQuestionFilledState: (questionId: string, filled: boolean) => void;

    /**
     * Define if the report form is in a submittable state
     */
    isSubmittable: boolean;
    /**
     * Map of questionId / boolean tracking if a particular question is in sync with the database
     */
    questionSyncStatusMap: Record<string, boolean>;
    /**
     * Set a question sync state
     *
     * Automatically define if the form is submitable
     */
    setQuestionSyncStatus: (questionId: string, inSync: boolean) => void;

    /**
     * Set to true if the viewer is currently focusing a text input
     *
     * This value is used to hide interaction that could be unintentionally triggered
     */
    textInputFocused: boolean;
    setTextInputFocused: (focused: boolean) => void;

    /**
     * [currentSectionId, shouldScroll]
     */
    currentSectionState: [string, boolean];
    setCurrentSectionState: (sectionId: string, shouldScroll: boolean) => void;
    /**
     * Current step the viewer is at within the report
     */
    currentStep: ReportStep;
    setCurrentStep: (step: ReportStep) => void;
    resetCurrentStep: (report: ReportFragment) => void;
    /**
     * The state of the report for the current viewer:
     *    - [0]: The role of the viewer on the report
     *        `(null | "collaborator" | "referent" | "supervisor")`
     *
     *
     *    - [1]: The state of the report for the viewer
     *        `(null | "view" | "edit")`
     */
    viewedReportState: [ReportRole, ReportState];

    /**
     * Reset the report store entirely, used when leaving the report page to avoid stale data to remain
     */
    reset: () => void;

    /**
     * Used to rehydrate the store with newly created answers between step change
     */
    rehydrateReportState: (report: ReportFragment) => void;
    initializeReportState: (report: ReportFragment) => void;
  };
};

const UNKNOWN: [ReportRole, ReportState] = [null, null];
const COLLABORATOR_VIEW: [ReportRole, ReportState] = ["collaborator", "view"];
const COLLABORATOR_EDIT: [ReportRole, ReportState] = ["collaborator", "edit"];
const REFERENT_VIEW: [ReportRole, ReportState] = ["referent", "view"];
const REFERENT_EDIT: [ReportRole, ReportState] = ["referent", "edit"];
const SUPERVISOR_VIEW: [ReportRole, ReportState] = ["supervisor", "view"];

function getReportViewerState(
  viewer?: ViewerFragment,
  report?: ReportFragment
) {
  if (!report || !viewer) return UNKNOWN;

  const viewerAsCollaborator = report.review.collaborator?.id === viewer?.id;
  const viewerAsReferent = report.reportReferents.find(
    (reportReferent) => reportReferent.referent.id === viewer?.id
  );
  const viewerAsSupervisor = report.review.reviewSupervisors.find(
    (supervisor) => supervisor.id === viewer?.id
  );

  const getViewerAsReferentRole = (): [ReportRole, ReportState] => {
    // The collaborator haven't filled yet the report
    if (report.filledAt === null) return REFERENT_VIEW;

    // The viewer have not yet filled his report
    if (viewerAsReferent?.filledAt === null) return REFERENT_EDIT;

    // The viewer is also supervisor or has the rights to read reviews,
    // it take precendence over referent once the report id filled
    if (viewerAsSupervisor || viewer.isCapableOfReadingReviews)
      return SUPERVISOR_VIEW;

    // Default to referent otherwise
    return REFERENT_VIEW;
  };

  // The current viewer is talent for the review
  if (viewerAsCollaborator) {
    // If the report has not yet been filled
    if (report.filledAt === null) return COLLABORATOR_EDIT;

    // The report has been filled, check if the user have another role within this review

    // The viewer is also a referent within the review
    if (viewerAsReferent) return getViewerAsReferentRole();

    // The viewer is also supervisor
    if (viewerAsSupervisor) return SUPERVISOR_VIEW;

    return COLLABORATOR_VIEW;
  }

  if (viewerAsReferent) return getViewerAsReferentRole();
  if (viewerAsSupervisor) return SUPERVISOR_VIEW;

  if (viewer.isCapableOfReadingReviews) return SUPERVISOR_VIEW;

  return UNKNOWN;
}

function getReportCurrentStep(
  reportViewerState: [ReportRole, ReportState],
  viewer?: ViewerFragment,
  report?: ReportFragment
): ReportStep {
  if (!report || !viewer) return null;

  if (reportViewerState[1] === "view") return "report";

  // If this is not the first report of the review or
  // the viewer have already answered some questions,
  // skip the welcome screen
  if (
    report.review.reportsCount > 1 ||
    report.answers.find((answer) => answer.user.id === viewer.id)
  )
    return "report";

  return "welcome";
}

export function getAggregatedSectionData(
  report: ReportFragment,
  viewerRole: ReportRole
): SectionWithAnswers[] {
  const completeTemplate = report?.template || report.review.template;
  const lastCheckedReport = report.review.lastCheckedReport;

  return (
    // Order sections based on their "order" property
    orderBy(completeTemplate.sections, "order")
      .map(({ questions, ...rest }) => ({
        ...rest,
        // Order questions based on their "order" property
        questions: orderBy(questions, "order")
          // Purge questions unrelevant to the viewer
          .filter((question) => {
            if (!question.reportTypes.includes(report.type)) return false;

            switch (question.visibility) {
              case "collaborator":
                return (
                  viewerRole === "collaborator" || viewerRole === "supervisor"
                );
              case "referents":
                return viewerRole === "referent" || viewerRole === "supervisor";
              case "all":
                return true;
              default:
                return false;
            }
          })
          // Merge previously collected answers with the question
          .map((question) => ({
            ...question,
            answers: report.answers.filter(
              ({ questionDigest }) => questionDigest === question.digest
            ),
            previousAnswers:
              lastCheckedReport?.answers.filter(
                (answer) => answer.questionId === question.id
              ) || [],
          })),
      }))
      // Purge empty sections
      ?.filter((section) => section.questions.length > 0) || []
  );
}

function getProgressMap(
  viewer?: ViewerFragment,
  report?: ReportFragment,
  sections?: SectionWithAnswers[]
): Record<string, boolean> {
  if (!report || !viewer || !sections) return {};

  return sections
    .flatMap(({ questions }) => questions.map(({ id }) => id))
    .reduce(
      (prev, key) => ({
        ...prev,
        [key]: !!report.answers.find(
          ({ questionId, isDraft, collaborator }) =>
            questionId === key && viewer.id === collaborator.id && isDraft
        ),
      }),
      {}
    );
}

function computeProgress(progressMap: Record<string, boolean>): number {
  const keys = Object.keys(progressMap);

  const questionsCount = keys.length;
  const answeredQuestionsCount = keys.reduce(
    (prev, key) => prev + (progressMap[key] ? 1 : 0),
    0
  );

  return Math.round((answeredQuestionsCount / questionsCount) * 100);
}

export const createReportSlice = (
  set: SetState<State>,
  get: GetState<State>
): ReportStore => ({
  report: {
    progress: 0,
    progressMap: {},
    setQuestionFilledState: (questionId, filled) =>
      set(
        produce((draftState: State) => {
          // If the question is unknown return
          if (draftState.report.progressMap[questionId] === undefined) return;

          draftState.report.progressMap[questionId] = filled;
          draftState.report.progress = computeProgress(
            draftState.report.progressMap
          );
        })
      ),

    isSubmittable: false,
    questionSyncStatusMap: {},

    setQuestionSyncStatus: (questionId, inSync) =>
      set(
        produce((draftState: State) => {
          // If the question is unknown return
          if (draftState.report.questionSyncStatusMap[questionId] === undefined)
            return;

          draftState.report.questionSyncStatusMap[questionId] = inSync;

          // Only allow submit when every questions are synced
          draftState.report.isSubmittable = Object.values(
            draftState.report.questionSyncStatusMap
          ).every((isSync) => isSync);
        })
      ),

    textInputFocused: false,
    setTextInputFocused: (focused) =>
      set(
        produce((draftState: State) => {
          draftState.report.textInputFocused = focused;
        })
      ),

    currentSectionState: ["", false],
    setCurrentSectionState: (sectionId, shouldScroll) =>
      set(
        produce((draftState: State) => {
          draftState.report.currentSectionState = [sectionId, shouldScroll];
        })
      ),

    currentStep: null,
    setCurrentStep: (step) =>
      set(
        produce((draftState: State) => {
          draftState.report.currentStep = step;
        })
      ),
    resetCurrentStep: (report) =>
      set(
        produce((draftState: State) => {
          const viewer = get().viewer;

          draftState.report.currentStep = getReportCurrentStep(
            draftState.report.viewedReportState,
            viewer,
            report
          );
        })
      ),

    viewedReportState: UNKNOWN,
    sections: [],
    reset: () =>
      set(
        produce((draftState: State) => {
          draftState.report.viewedReportState = UNKNOWN;
        })
      ),
    rehydrateReportState: (report) =>
      set(
        produce((draftState: State) => {
          draftState.report.sections = getAggregatedSectionData(
            report,
            draftState.report.viewedReportState[0]
          );
        })
      ),
    initializeReportState: (report) =>
      set(
        produce((draftState: State) => {
          const viewer = get().viewer;

          draftState.report.viewedReportState = getReportViewerState(
            viewer,
            report
          );

          draftState.report.sections = getAggregatedSectionData(
            report,
            draftState.report.viewedReportState[0]
          );

          draftState.report.currentStep = getReportCurrentStep(
            draftState.report.viewedReportState,
            viewer,
            report
          );

          draftState.report.currentSectionState = [
            draftState.report.sections[0]?.id || "",
            false,
          ];

          draftState.report.progressMap = getProgressMap(
            viewer,
            report,
            draftState.report.sections
          );

          draftState.report.progress = computeProgress(
            draftState.report.progressMap
          );

          draftState.report.questionSyncStatusMap = draftState.report.sections
            .flatMap(({ questions }) => questions.map(({ id }) => id))
            .reduce(
              (prev, key) => ({
                ...prev,
                [key]: false,
              }),
              {}
            );
        })
      ),
  },
});
